/**
 * Typed wrapper for the public NIPOST outlet directory.
 *
 * Endpoint: `GET {VITE_SERVICE_CUSTOMER_BASE_URL}/v1/outlets`
 *
 * Auth: none — backed by the `@Public()` OutletController on
 * service-customer (mirrors the public tracking endpoint pattern).
 */
import { requestJson, type RequestJsonOptions } from '../api-client';
import { env } from '../env';

/** Service codes that the seed data emits. The backend is permissive — any
 *  string is accepted — but these are the values we render labels for. */
export type OutletService = 'DROPOFF' | 'PICKUP' | 'PAYMENT' | 'KYC' | string;

export interface Outlet {
  readonly id: string;
  readonly outletCode: string;
  readonly name: string;
  readonly addressLine1: string;
  readonly addressLine2: string | null;
  readonly city: string;
  readonly lga: string;
  readonly state: string;
  readonly postalCode: string | null;
  readonly phone: string | null;
  readonly email: string | null;
  readonly latitude: string | null;
  readonly longitude: string | null;
  readonly services: readonly OutletService[];
  readonly openingHours: Readonly<Record<string, string>>;
  readonly isActive: boolean;
}

export interface ListOutletsResponse {
  readonly items: readonly Outlet[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface ListOutletsRequest {
  readonly q?: string;
  readonly state?: string;
  readonly lga?: string;
  readonly city?: string;
  readonly service?: OutletService;
  readonly page?: number;
  readonly pageSize?: number;
}

/** Build the query string lazily so callers pass an undefined-friendly object.
 *  Empty / undefined values are dropped so the backend sees a clean query. */
function buildQuery(params: ListOutletsRequest): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    const str = typeof value === 'string' ? value.trim() : String(value);
    if (str.length === 0) continue;
    search.set(key, str);
  }
  const qs = search.toString();
  return qs.length > 0 ? `?${qs}` : '';
}

export async function listOutlets(
  params: ListOutletsRequest = {},
  options: Pick<RequestJsonOptions, 'fetchImpl' | 'signal'> = {},
): Promise<ListOutletsResponse> {
  return requestJson<ListOutletsResponse>({
    method: 'GET',
    baseUrl: env.serviceCustomerBaseUrl,
    path: `/v1/outlets${buildQuery(params)}`,
    suppressAuthRefresh: true,
    ...options,
  });
}
